import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdHistory, type AdGeneration } from '../../hooks/useAdHistory';
import { TagManager, TagBadge, getTagColor } from './components/TagManager';
import SocialPostHistoryList from './components/SocialPostHistoryList';
import type { AdResult } from '../../mocks/adResults';
import { downloadFile } from '../../lib/download';

const platformColors: Record<string, string> = {
  Facebook: 'bg-blue-50 text-blue-600 border-blue-100',
  Instagram: 'bg-pink-50 text-pink-600 border-pink-100',
  Google: 'bg-amber-50 text-amber-700 border-amber-100',
  TikTok: 'bg-stone-100 text-stone-700 border-stone-200',
  LinkedIn: 'bg-sky-50 text-sky-700 border-sky-100',
};

const imageLabels = ['Scene', 'Lifestyle', 'Abstract'];

/* ─── Copy Button ─────────────────────────────────────────────────────────── */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer shrink-0 ${
        copied ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500'
      }`}
    >
      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
    </button>
  );
}

/* ─── Checkbox ────────────────────────────────────────────────────────────── */
function SelectCheckbox({ checked, indeterminate = false, onChange }: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-150 cursor-pointer ${
        checked || indeterminate
          ? 'bg-orange-500 border-orange-500 text-white'
          : 'bg-white border-stone-300 hover:border-orange-400'
      }`}
    >
      {indeterminate && !checked
        ? <i className="ri-subtract-line text-xs" />
        : checked
        ? <i className="ri-check-line text-xs" />
        : null}
    </button>
  );
}

/* ─── Image Thumbnails ────────────────────────────────────────────────────── */
function SavedVideoPlayer({ urls }: { urls: string[] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const url = urls[activeIdx];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-5 h-5 flex items-center justify-center rounded-md bg-rose-100">
          <i className="ri-film-fill text-rose-500 text-xs" />
        </div>
        <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">Saved Video Ads</span>
        <span className="text-xs text-stone-400 ml-auto">{urls.length} video{urls.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="rounded-xl overflow-hidden bg-black border border-stone-200">
        <video
          key={url}
          src={url}
          controls
          loop
          playsInline
          className="w-full max-h-64 object-contain"
        />
      </div>
      {urls.length > 1 && (
        <div className="flex gap-1.5">
          {urls.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIdx(idx)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                activeIdx === idx
                  ? 'border-rose-400 bg-rose-50 text-rose-600'
                  : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
              }`}
            >
              Video {idx + 1}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => downloadFile(url, `video-ad-${activeIdx + 1}.mp4`)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 text-white text-xs font-bold hover:from-stone-800 hover:to-stone-700 transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-download-2-line" />
        Download Video{urls.length > 1 ? ` ${activeIdx + 1}` : ''}
      </button>
    </div>
  );
}

function ImageThumbnails({ urls }: { urls: string[] }) {
  const [active, setActive] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <div className="space-y-2">
      <div
        className="relative w-full rounded-xl overflow-hidden bg-stone-100 cursor-zoom-in"
        style={{ aspectRatio: '1 / 1' }}
        onClick={() => setFullscreen(true)}
      >
        <img
          src={urls[active]}
          alt={imageLabels[active] ?? 'Ad image'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
          {imageLabels[active] ?? `Image ${active + 1}`}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/10">
          <div className="bg-white/80 rounded-full w-8 h-8 flex items-center justify-center">
            <i className="ri-zoom-in-line text-stone-700 text-sm" />
          </div>
        </div>
      </div>

      {urls.length > 1 && (
        <div className="flex gap-1.5">
          {urls.map((url, idx) => (
            <button
              key={idx}
              onClick={() => setActive(idx)}
              className={`flex-1 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                active === idx ? 'border-orange-500' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              style={{ aspectRatio: '1 / 1' }}
            >
              <img
                src={url}
                alt={imageLabels[idx] ?? `Image ${idx + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setFullscreen(false)}
        >
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setFullscreen(false)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white cursor-pointer flex items-center gap-1 text-sm"
            >
              <i className="ri-close-line" /> Close
            </button>
            <img
              src={urls[active]}
              alt={imageLabels[active] ?? 'Ad image'}
              className="w-full rounded-xl"
            />
            {urls.length > 1 && (
              <div className="flex gap-2 mt-3 justify-center">
                {urls.map((url, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActive(idx)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      active === idx ? 'border-orange-500' : 'border-white/30 hover:border-white/60'
                    }`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Convert history item → AdResult for the generate session ────────────── */
function historyToAdResult(item: AdGeneration): AdResult {
  return {
    hooks: item.hooks ?? [],
    headlines: item.headlines ?? [],
    subheadlines: item.subheadlines ?? [],
    primaryText: item.primary_text ?? [],
    ctas: item.ctas ?? [],
    overlayText: item.overlay_text ?? undefined,
    imageUrls: item.image_storage_urls ?? [],
    imageStorageUrls: item.image_storage_urls ?? [],
  };
}

/* ─── Copy sections helper ────────────────────────────────────────────────── */
const COPY_SECTIONS = [
  { label: 'Hooks', key: 'hooks' as const, icon: 'ri-fire-line', color: 'text-orange-500' },
  { label: 'Headlines', key: 'headlines' as const, icon: 'ri-heading', color: 'text-amber-600' },
  { label: 'Sub Headlines', key: 'subheadlines' as const, icon: 'ri-text', color: 'text-teal-600' },
  { label: 'Ad Copy', key: 'primary_text' as const, icon: 'ri-paragraph', color: 'text-rose-500' },
  { label: 'CTAs', key: 'ctas' as const, icon: 'ri-cursor-fill', color: 'text-orange-500' },
];

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export default function HistoryPage() {
  const navigate = useNavigate();
  const { history, loading, error: historyError, refetch, deleteGeneration, bulkDeleteGenerations, updateTags } = useAdHistory(50);

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'ads' | 'social' | 'videos'>('ads');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  // Tag filter state
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  const allUsedTags = useMemo(
    () => Array.from(new Set(history.flatMap(item => item.tags ?? []))).sort(),
    [history]
  );

  const filteredHistory = useMemo(() => {
    if (activeTags.size === 0) return history;
    return history.filter(item =>
      Array.from(activeTags).every(tag => (item.tags ?? []).includes(tag))
    );
  }, [history, activeTags]);

  const toggleTagFilter = useCallback((tag: string) => {
    setActiveTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  }, []);

  // Videos-only list
  const videoHistory = useMemo(
    () => history.filter(item => item.video_storage_urls && item.video_storage_urls.length > 0),
    [history]
  );

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handleDeleteConfirm = async (id: string) => {
    setDeletingId(id);
    setDeleteConfirmId(null);
    try {
      await deleteGeneration(id);
      if (expandedId === id) setExpandedId(null);
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelectMode = () => {
    setSelectMode((m) => !m);
    setSelectedIds(new Set());
    setShowBulkConfirm(false);
    setDeleteConfirmId(null);
    setExpandedId(null);
  };

  const toggleItem = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const isAllSelected = history.length > 0 && selectedIds.size === history.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(history.map((h) => h.id)));
    }
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    setShowBulkConfirm(false);
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteGenerations(ids);
      setSelectedIds(new Set());
      setSelectMode(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleLoadForEditing = useCallback((item: AdGeneration) => {
    navigate('/dashboard/generate', {
      state: { historyItem: item },
    });
  }, [navigate]);

  return (
    <div className="flex-1 overflow-y-auto">
      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="bg-white border-b border-stone-100 shrink-0 sticky top-0 z-10">
        {selectMode ? (
          /* Select mode header */
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 min-h-[56px]">
            <SelectCheckbox
              checked={isAllSelected}
              indeterminate={isSomeSelected}
              onChange={toggleSelectAll}
            />
            <span className="text-sm font-bold text-stone-800">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select items'}
            </span>
            <button
              onClick={toggleSelectAll}
              className="text-xs text-stone-500 hover:text-orange-500 transition-colors cursor-pointer underline underline-offset-2 whitespace-nowrap"
            >
              {isAllSelected ? 'Deselect all' : 'Select all'}
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-2 shrink-0">
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setShowBulkConfirm(true)}
                  disabled={isBulkDeleting}
                  className="flex items-center gap-1.5 text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  <i className="ri-delete-bin-line" />
                  <span className="hidden sm:inline">Delete </span>{selectedIds.size}
                </button>
              )}
              <button
                onClick={toggleSelectMode}
                className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* Normal header */
          <div className="px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:h-16 sm:py-0">
            {/* Left: title + tab toggle */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <div className="hidden sm:block">
                <h1 className="text-base font-bold text-stone-900">
                  {activeTab === 'ads' ? 'Ad History' : activeTab === 'videos' ? 'Video History' : 'Social Post History'}
                </h1>
                <p className="text-xs text-stone-400">
                  {activeTab === 'ads' ? 'All your generated campaigns, auto-saved' : activeTab === 'videos' ? 'All your generated video ads, auto-saved' : 'All your generated social posts, auto-saved'}
                </p>
              </div>
              {/* Tab toggle */}
              <div className="flex items-center bg-stone-100 rounded-xl p-1 gap-0.5 w-full sm:w-auto">
                <button
                  onClick={() => { setActiveTab('ads'); setExpandedId(null); setSelectMode(false); setSelectedIds(new Set()); }}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    activeTab === 'ads'
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className="ri-advertisement-line text-sm" />
                  Ads
                </button>
                <button
                  onClick={() => { setActiveTab('videos'); setExpandedId(null); setSelectMode(false); setSelectedIds(new Set()); }}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    activeTab === 'videos'
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className="ri-film-line text-sm" />
                  Videos
                  {videoHistory.length > 0 && (
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
                      activeTab === 'videos' ? 'bg-rose-100 text-rose-600' : 'bg-stone-200 text-stone-500'
                    }`}>
                      {videoHistory.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => { setActiveTab('social'); setExpandedId(null); setSelectMode(false); setSelectedIds(new Set()); }}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    activeTab === 'social'
                      ? 'bg-white text-stone-900 shadow-sm'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className="ri-instagram-line text-sm" />
                  Social Posts
                </button>
              </div>
            </div>

            {/* Right: action buttons */}
            {activeTab === 'ads' && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs text-stone-400 bg-stone-100 px-3 py-1.5 rounded-full font-semibold whitespace-nowrap">
                  {activeTags.size > 0
                    ? `${filteredHistory.length} of ${history.length}`
                    : `${history.length} gen${history.length !== 1 ? 's' : ''}`
                  }
                </div>
                <button
                  onClick={() => refetch()}
                  disabled={loading}
                  title="Refresh history"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone-200 text-stone-400 hover:text-orange-500 hover:border-orange-300 hover:bg-orange-50 transition-all duration-150 cursor-pointer disabled:opacity-40 shrink-0"
                >
                  <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
                </button>
                {history.length > 0 && (
                  <>
                    <button
                      onClick={toggleSelectMode}
                      className="flex items-center gap-1.5 text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 border border-stone-200 px-3 py-1.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-checkbox-multiple-line text-sm" />
                      <span className="hidden sm:inline">Select</span>
                    </button>
                    <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden shrink-0">
                      <button
                        onClick={() => setView('grid')}
                        className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${view === 'grid' ? 'bg-stone-900 text-white' : 'text-stone-400 hover:text-stone-700'}`}
                      >
                        <i className="ri-grid-line text-sm" />
                      </button>
                      <button
                        onClick={() => setView('list')}
                        className={`w-8 h-8 flex items-center justify-center transition-colors cursor-pointer ${view === 'list' ? 'bg-stone-900 text-white' : 'text-stone-400 hover:text-stone-700'}`}
                      >
                        <i className="ri-list-check text-sm" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {activeTab === 'videos' && (
              <div className="flex items-center gap-2">
                <div className="text-xs text-stone-400 bg-stone-100 px-3 py-1.5 rounded-full font-semibold whitespace-nowrap">
                  {videoHistory.length} video{videoHistory.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => refetch()}
                  disabled={loading}
                  title="Refresh"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-stone-200 text-stone-400 hover:text-rose-500 hover:border-rose-300 hover:bg-rose-50 transition-all duration-150 cursor-pointer disabled:opacity-40 shrink-0"
                >
                  <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── Bulk delete confirmation bar ──────────────────────────────── */}
      {showBulkConfirm && activeTab === 'ads' && (
        <div className="sticky top-[56px] sm:top-16 z-10 mx-4 mt-3">
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 shrink-0">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <div>
                <p className="text-sm font-bold text-stone-900">
                  Delete {selectedIds.size} generation{selectedIds.size !== 1 ? 's' : ''}?
                </p>
                <p className="text-xs text-stone-500 mt-0.5">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowBulkConfirm(false)}
                className="text-xs font-semibold text-stone-500 hover:text-stone-700 px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="flex items-center gap-1.5 text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {isBulkDeleting ? (
                  <><i className="ri-loader-4-line animate-spin" /> Deleting...</>
                ) : (
                  <><i className="ri-delete-bin-line" /> Yes, delete all</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* ── Social Posts Tab ──────────────────────────────────────── */}
        {activeTab === 'social' ? (
          <SocialPostHistoryList />
        ) : activeTab === 'videos' ? (
          /* ── Videos Tab ─────────────────────────────────────────── */
          <VideoHistoryTab
            videoHistory={videoHistory}
            loading={loading}
            onEditVideo={(item, videoUrl, audioUrl, voiceoverScript) => {
              navigate('/dashboard/video-editor', {
                state: {
                  videoUrl,
                  audioUrl,
                  voiceoverScript,
                  aspectRatio: '9:16',
                  videoId: item.id,
                },
              });
            }}
            onDelete={(id) => { setDeleteConfirmId(id); }}
            deleteConfirmId={deleteConfirmId}
            deletingId={deletingId}
            onDeleteConfirm={handleDeleteConfirm}
            onDeleteCancel={() => setDeleteConfirmId(null)}
            formatDate={formatDate}
            formatTime={formatTime}
          />
        ) : (
          <>
            {historyError && (
              <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                <i className="ri-error-warning-line shrink-0" />
                <span className="flex-1">Could not load history: {historyError}</span>
                <button onClick={() => refetch()} className="text-xs font-bold underline cursor-pointer whitespace-nowrap">Retry</button>
              </div>
            )}

            {/* ── Tag filter bar ─────────────────────────────────────── */}
            {allUsedTags.length > 0 && !selectMode && (
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider shrink-0">Filter:</span>
                <button
                  onClick={() => setActiveTags(new Set())}
                  className={`text-xs font-bold px-3 py-1 rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    activeTags.size === 0
                      ? 'bg-stone-800 border-stone-800 text-white'
                      : 'bg-white border-stone-200 text-stone-500 hover:border-stone-400'
                  }`}
                >
                  All
                </button>
                {allUsedTags.map(tag => {
                  const c = getTagColor(tag);
                  const isActive = activeTags.has(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => toggleTagFilter(tag)}
                      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                        isActive
                          ? `${c.bg} ${c.text} ${c.border}`
                          : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? c.dot : 'bg-stone-300'}`} />
                      {tag}
                    </button>
                  );
                })}
                {activeTags.size > 0 && (
                  <button
                    onClick={() => setActiveTags(new Set())}
                    className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-close-line text-xs" /> Clear filters
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="bg-white rounded-2xl border border-stone-100 animate-pulse">
                    <div className="aspect-square bg-stone-100 rounded-t-2xl" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-stone-100 rounded w-3/4" />
                      <div className="h-3 bg-stone-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-stone-100 mb-5">
                  <i className="ri-history-line text-stone-400 text-4xl" />
                </div>
                <h2 className="text-lg font-bold text-stone-800 mb-2">No ad history yet</h2>
                <p className="text-sm text-stone-500 max-w-sm leading-relaxed mb-6">
                  Generate your first ad and it&apos;ll be automatically saved here — images and all.
                </p>
                <Link
                  to="/dashboard/generate"
                  className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-400 text-white text-sm font-bold px-5 py-3 rounded-xl hover:from-orange-600 hover:to-amber-500 transition-all whitespace-nowrap"
                >
                  <i className="ri-flashlight-fill" /> Generate Your First Ad
                </Link>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-stone-100 mb-4">
                  <i className="ri-price-tag-3-line text-stone-400 text-3xl" />
                </div>
                <h2 className="text-base font-bold text-stone-800 mb-1.5">No campaigns match these tags</h2>
                <p className="text-sm text-stone-500 max-w-xs leading-relaxed mb-4">
                  Try removing some filters or add tags to your existing campaigns.
                </p>
                <button
                  onClick={() => setActiveTags(new Set())}
                  className="text-sm font-bold text-orange-500 hover:text-orange-600 transition-colors cursor-pointer"
                >
                  Clear all filters
                </button>
              </div>
            ) : view === 'grid' ? (
              /* ── Grid View ──────────────────────────────────────────────── */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredHistory.map(item => {
                  const isExpanded = expandedId === item.id;
                  const colorClass = platformColors[item.platform ?? ''] ?? 'bg-stone-100 text-stone-600 border-stone-200';
                  const hasImages = item.image_storage_urls && item.image_storage_urls.length > 0;
                  const hasVideos = item.video_storage_urls && item.video_storage_urls.length > 0;
                  const isConfirmingDelete = deleteConfirmId === item.id;
                  const isDeleting = deletingId === item.id;
                  const isSelected = selectedIds.has(item.id);

                  return (
                    <div
                      key={item.id}
                      onClick={selectMode ? () => toggleItem(item.id) : undefined}
                      className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 flex flex-col group ${
                        isDeleting ? 'opacity-40 pointer-events-none'
                        : selectMode
                          ? isSelected
                            ? 'border-orange-400 cursor-pointer'
                            : 'border-stone-200 hover:border-orange-200 cursor-pointer'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                    >
                      {/* Image area */}
                      <div className="relative">
                        {hasImages ? (
                          <div className={`p-3 pb-0 ${selectMode ? 'pointer-events-none' : ''}`}>
                            <ImageThumbnails urls={item.image_storage_urls!} />
                          </div>
                        ) : (
                          <div className="aspect-square bg-gradient-to-br from-stone-100 to-stone-50 flex items-center justify-center">
                            <div className="text-center">
                              <i className="ri-image-line text-stone-300 text-4xl" />
                              <p className="text-xs text-stone-300 mt-1">No image saved</p>
                            </div>
                          </div>
                        )}

                        {selectMode && (
                          <div className="absolute top-5 left-5 z-10">
                            <SelectCheckbox checked={isSelected} onChange={() => toggleItem(item.id)} />
                          </div>
                        )}
                        {selectMode && isSelected && (
                          <div className="absolute inset-0 bg-orange-400/10 pointer-events-none" />
                        )}
                        {!selectMode && !isConfirmingDelete && !isDeleting && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(item.id); }}
                            title="Delete this generation"
                            className="absolute top-5 right-5 w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 text-stone-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all duration-150 cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </div>

                      {/* Card body */}
                      <div className={`p-4 flex-1 flex flex-col ${selectMode ? 'pointer-events-none' : ''}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-stone-900 truncate">
                              {item.business_name ?? 'Unknown Business'}
                            </div>
                            <div className="text-xs text-stone-400">{formatDate(item.created_at)} · {formatTime(item.created_at)}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border ${colorClass}`}>
                              {item.platform ?? 'Ad'}
                            </span>
                            {hasVideos && (
                              <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-lg border bg-rose-50 text-rose-600 border-rose-200">
                                <i className="ri-film-fill text-xs" />
                                {item.video_storage_urls!.length}v
                              </span>
                            )}
                          </div>
                        </div>

                        {item.headlines && item.headlines.length > 0 && (
                          <p className="text-xs text-stone-600 leading-relaxed line-clamp-2 mb-2">
                            {item.headlines[0]}
                          </p>
                        )}

                        {!selectMode && (
                          <div className="mb-3 flex-1">
                            <TagManager
                              itemId={item.id}
                              tags={item.tags ?? []}
                              allExistingTags={allUsedTags}
                              onUpdate={updateTags}
                            />
                          </div>
                        )}

                        {isConfirmingDelete && !selectMode ? (
                          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                            <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
                            <span className="text-xs text-red-700 font-medium flex-1">Delete this generation?</span>
                            <button
                              onClick={() => handleDeleteConfirm(item.id)}
                              className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Delete
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                            >
                              <i className="ri-close-line" />
                            </button>
                          </div>
                        ) : !selectMode ? (
                          <div className="flex flex-col gap-1.5">
                            {hasImages && (
                              <button
                                onClick={() => handleLoadForEditing(item)}
                                className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 transition-all py-2 rounded-xl cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-magic-line" />
                                Load &amp; Edit Images
                              </button>
                            )}
                            {hasVideos && (
                              <button
                                onClick={() => setExpandedId(isExpanded && expandedId === item.id ? null : item.id)}
                                className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-all py-2 rounded-xl cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-film-fill" />
                                Watch Saved Video{item.video_storage_urls!.length > 1 ? 's' : ''}
                              </button>
                            )}
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : item.id)}
                              className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-orange-500 transition-colors py-2 border border-stone-100 rounded-xl hover:border-orange-200 hover:bg-orange-50/50 cursor-pointer"
                            >
                              <i className={isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                              {isExpanded ? 'Hide copy' : 'View full copy'}
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {isExpanded && !isConfirmingDelete && !selectMode && (
                        <div className="border-t border-stone-100 px-4 py-4 space-y-4">
                          {hasVideos && (
                            <SavedVideoPlayer urls={item.video_storage_urls!} />
                          )}
                          {item.offer && (
                            <div className="bg-orange-50 rounded-xl px-3 py-2.5 border border-orange-100">
                              <div className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-0.5">Offer</div>
                              <p className="text-xs text-stone-700">{item.offer}</p>
                            </div>
                          )}
                          {COPY_SECTIONS.map(section => {
                            const items = item[section.key] ?? [];
                            if (!items.length) return null;
                            return (
                              <div key={section.label}>
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <i className={`${section.icon} ${section.color} text-xs`} />
                                  <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">{section.label}</span>
                                </div>
                                <div className="space-y-1.5">
                                  {items.map((text, idx) => (
                                    <div key={idx} className="flex items-start gap-2 bg-stone-50 rounded-lg px-3 py-2 border border-stone-100">
                                      <p className="text-xs text-stone-700 flex-1 leading-relaxed">{text}</p>
                                      <CopyButton text={text} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── List View ──────────────────────────────────────────────── */
              <div className="space-y-3">
                {filteredHistory.map(item => {
                  const isExpanded = expandedId === item.id;
                  const colorClass = platformColors[item.platform ?? ''] ?? 'bg-stone-100 text-stone-600 border-stone-200';
                  const hasImages = item.image_storage_urls && item.image_storage_urls.length > 0;
                  const hasVideos = item.video_storage_urls && item.video_storage_urls.length > 0;
                  const isConfirmingDelete = deleteConfirmId === item.id;
                  const isDeleting = deletingId === item.id;
                  const isSelected = selectedIds.has(item.id);

                  return (
                    <div
                      key={item.id}
                      className={`bg-white rounded-2xl border-2 overflow-hidden transition-all duration-200 ${
                        isDeleting ? 'opacity-40 pointer-events-none'
                        : selectMode
                          ? isSelected
                            ? 'border-orange-400'
                            : 'border-stone-200 hover:border-orange-200'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                    >
                      <div
                        className={`flex items-center gap-3 px-4 py-3 ${selectMode ? 'cursor-pointer' : ''}`}
                        onClick={selectMode ? () => toggleItem(item.id) : undefined}
                      >
                        {selectMode && (
                          <SelectCheckbox checked={isSelected} onChange={() => toggleItem(item.id)} />
                        )}

                        <div
                          onClick={selectMode ? undefined : () => setExpandedId(isExpanded ? null : item.id)}
                          className={`w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-stone-100 flex items-center justify-center ${!selectMode ? 'cursor-pointer' : ''}`}
                        >
                          {hasImages ? (
                            <img
                              src={item.image_storage_urls![0]}
                              alt="Ad"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <i className="ri-image-line text-stone-300 text-xl" />
                          )}
                        </div>

                        <div
                          className={`flex items-center gap-2 flex-1 min-w-0 text-left ${!selectMode ? 'cursor-pointer' : ''}`}
                          onClick={selectMode ? undefined : () => setExpandedId(isExpanded ? null : item.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border shrink-0 ${colorClass}`}>
                                {item.platform ?? 'Ad'}
                              </span>
                              <div className="text-sm font-bold text-stone-900 truncate min-w-0">
                                {item.business_name ?? 'Unknown Business'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <div className="text-xs text-stone-400 truncate">{formatDate(item.created_at)}</div>
                              {hasVideos && !selectMode && (
                                <span className="flex items-center gap-0.5 text-xs font-bold text-rose-500 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full shrink-0">
                                  <i className="ri-film-fill text-xs" />
                                  {item.video_storage_urls!.length}
                                </span>
                              )}
                            </div>
                          </div>
                          {!selectMode && (
                            <i className={`ri-${isExpanded ? 'arrow-up' : 'arrow-down'}-s-line text-stone-400 shrink-0`} />
                          )}
                        </div>

                        {!selectMode && (
                          <div className="shrink-0 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {hasImages && !isConfirmingDelete && (
                              <button
                                onClick={() => handleLoadForEditing(item)}
                                title="Load into generator to edit images"
                                className="flex items-center gap-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 px-2.5 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-magic-line text-xs" />
                                <span className="hidden sm:inline">Edit</span>
                              </button>
                            )}

                            {isConfirmingDelete ? (
                              <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-2.5 py-1.5">
                                <span className="text-xs text-red-700 font-medium whitespace-nowrap">Delete?</span>
                                <button
                                  onClick={() => handleDeleteConfirm(item.id)}
                                  className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(null)}
                                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                                >
                                  <i className="ri-close-line" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmId(item.id)}
                                title="Delete this generation"
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-transparent text-stone-400 hover:border-red-200 hover:text-red-500 hover:bg-red-50 transition-all duration-150 cursor-pointer"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {isExpanded && !selectMode && (
                        <div className="border-t border-stone-100 px-4 py-5 space-y-5">
                          <div>
                            <div className="flex items-center gap-1.5 mb-2">
                              <i className="ri-price-tag-3-line text-stone-400 text-sm" />
                              <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">Tags</span>
                            </div>
                            <TagManager
                              itemId={item.id}
                              tags={item.tags ?? []}
                              allExistingTags={allUsedTags}
                              onUpdate={updateTags}
                            />
                          </div>
                          {hasVideos && (
                            <SavedVideoPlayer urls={item.video_storage_urls!} />
                          )}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {hasImages && (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <i className="ri-image-line text-orange-400 text-sm" />
                                  <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">Ad Images</span>
                                </div>
                                <ImageThumbnails urls={item.image_storage_urls!} />
                              </div>
                            )}
                            <div className="space-y-4">
                              <div className="flex flex-wrap gap-2">
                                {item.business_type && (
                                  <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 font-medium">{item.business_type}</span>
                                )}
                                {item.tone && (
                                  <span className="text-xs px-2.5 py-1 rounded-full bg-orange-50 text-orange-600 font-medium capitalize">{item.tone} tone</span>
                                )}
                                {item.audience && (
                                  <span className="text-xs px-2.5 py-1 rounded-full bg-stone-100 text-stone-500 font-medium">{item.audience}</span>
                                )}
                              </div>
                              {item.offer && (
                                <div className="bg-stone-50 rounded-xl px-4 py-3 border border-stone-100">
                                  <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Offer</div>
                                  <p className="text-sm text-stone-700">{item.offer}</p>
                                </div>
                              )}
                              {COPY_SECTIONS.map(section => {
                                const items = item[section.key] ?? [];
                                if (!items.length) return null;
                                return (
                                  <div key={section.label}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <i className={`${section.icon} ${section.color} text-sm`} />
                                      <span className="text-xs font-bold text-stone-700 uppercase tracking-wider">{section.label}</span>
                                    </div>
                                    <div className="space-y-2">
                                      {items.map((text, idx) => (
                                        <div key={idx} className="flex items-start gap-3 bg-stone-50 rounded-xl px-4 py-3 border border-stone-100">
                                          <p className="text-sm text-stone-700 flex-1 leading-relaxed">{text}</p>
                                          <CopyButton text={text} />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Video History Tab ───────────────────────────────────────────────────── */
interface VideoHistoryTabProps {
  videoHistory: AdGeneration[];
  loading: boolean;
  onEditVideo: (item: AdGeneration, videoUrl: string, audioUrl: string | null, voiceoverScript: string | null) => void;
  onDelete: (id: string) => void;
  deleteConfirmId: string | null;
  deletingId: string | null;
  onDeleteConfirm: (id: string) => void;
  onDeleteCancel: () => void;
  formatDate: (iso: string) => string;
  formatTime: (iso: string) => string;
}

function VideoHistoryTab({
  videoHistory,
  loading,
  onEditVideo,
  onDelete,
  deleteConfirmId,
  deletingId,
  onDeleteConfirm,
  onDeleteCancel,
  formatDate,
  formatTime,
}: VideoHistoryTabProps) {
  const [activeVideoIdx, setActiveVideoIdx] = useState<Record<string, number>>({});

  const getActiveIdx = (id: string) => activeVideoIdx[id] ?? 0;
  const setActiveIdx = (id: string, idx: number) =>
    setActiveVideoIdx(prev => ({ ...prev, [id]: idx }));

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-stone-100 animate-pulse">
            <div className="aspect-video bg-stone-100 rounded-t-2xl" />
            <div className="p-4 space-y-2">
              <div className="h-4 bg-stone-100 rounded w-3/4" />
              <div className="h-3 bg-stone-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (videoHistory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-28 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-rose-50 mb-5">
          <i className="ri-film-line text-rose-300 text-4xl" />
        </div>
        <h2 className="text-lg font-bold text-stone-800 mb-2">No video ads yet</h2>
        <p className="text-sm text-stone-500 max-w-sm leading-relaxed mb-6">
          Generate a video ad from the dashboard and it&apos;ll appear here automatically.
        </p>
        <Link
          to="/dashboard/generate"
          className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-orange-400 text-white text-sm font-bold px-5 py-3 rounded-xl hover:from-rose-600 hover:to-orange-500 transition-all whitespace-nowrap"
        >
          <i className="ri-film-fill" /> Create Your First Video Ad
        </Link>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {videoHistory.map(item => {
        const urls = item.video_storage_urls!;
        const activeIdx = getActiveIdx(item.id);
        const activeUrl = urls[activeIdx];
        const activeAudioUrl = item.video_audio_urls?.[activeIdx] || null;
        const activeVoiceoverScript = item.video_voiceover_scripts?.[activeIdx] || null;
        const isConfirmingDelete = deleteConfirmId === item.id;
        const isDeleting = deletingId === item.id;
        const colorClass = platformColors[item.platform ?? ''] ?? 'bg-stone-100 text-stone-600 border-stone-200';

        return (
          <div
            key={item.id}
            className={`bg-white rounded-2xl border-2 overflow-hidden flex flex-col group transition-all duration-200 ${
              isDeleting ? 'opacity-40 pointer-events-none' : 'border-stone-100 hover:border-rose-200'
            }`}
          >
            {/* Video player */}
            <div className="relative bg-black rounded-t-2xl overflow-hidden">
              <video
                key={activeUrl}
                src={activeUrl}
                controls
                loop
                playsInline
                className="w-full aspect-video object-contain"
              />
              {/* Delete button */}
              {!isConfirmingDelete && (
                <button
                  onClick={() => onDelete(item.id)}
                  title="Delete"
                  className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-lg bg-black/50 text-white/70 hover:bg-red-500 hover:text-white transition-all duration-150 cursor-pointer opacity-0 group-hover:opacity-100 z-10"
                >
                  <i className="ri-delete-bin-line text-xs" />
                </button>
              )}
              {/* Video count badge */}
              {urls.length > 1 && (
                <div className="absolute top-2.5 left-2.5 bg-black/60 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeIdx + 1} / {urls.length}
                </div>
              )}
            </div>

            {/* Multi-video switcher */}
            {urls.length > 1 && (
              <div className="flex gap-1.5 px-3 pt-3">
                {urls.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveIdx(item.id, idx)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${
                      activeIdx === idx
                        ? 'border-rose-400 bg-rose-50 text-rose-600'
                        : 'border-stone-200 bg-white text-stone-500 hover:border-stone-300'
                    }`}
                  >
                    Video {idx + 1}
                  </button>
                ))}
              </div>
            )}

            {/* Card info */}
            <div className="p-3 flex-1 flex flex-col gap-2">
              {isConfirmingDelete ? (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
                  <span className="text-xs text-red-700 font-medium flex-1">Delete this video?</span>
                  <button
                    onClick={() => onDeleteConfirm(item.id)}
                    className="text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Delete
                  </button>
                  <button
                    onClick={onDeleteCancel}
                    className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-stone-900 truncate">
                        {item.business_name ?? 'Unknown Business'}
                      </div>
                      <div className="text-xs text-stone-400 mt-0.5">
                        {formatDate(item.created_at)} · {formatTime(item.created_at)}
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-lg border shrink-0 ${colorClass}`}>
                      {item.platform ?? 'Ad'}
                    </span>
                  </div>

                  {item.headlines && item.headlines.length > 0 && (
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-2">
                      {item.headlines[0]}
                    </p>
                  )}

                  {activeAudioUrl && (
                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-2.5 space-y-2">
                      <div className="flex items-start gap-2">
                        <i className="ri-mic-line text-orange-500 text-sm mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-orange-800">Voiceover saved</p>
                          {activeVoiceoverScript && (
                            <p className="text-xs text-orange-700 line-clamp-2 mt-0.5">{activeVoiceoverScript}</p>
                          )}
                        </div>
                      </div>
                      <audio src={activeAudioUrl} controls className="w-full h-8" />
                    </div>
                  )}

                  <div className="mt-auto grid grid-cols-2 gap-2">
                    <button
                      onClick={() => onEditVideo(item, activeUrl, activeAudioUrl, activeVoiceoverScript)}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-orange-400 text-white text-xs font-bold hover:from-rose-600 hover:to-orange-500 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-2-line" />
                      Edit
                    </button>
                    <button
                      onClick={() => downloadFile(activeUrl, `video-ad-${item.id.slice(0, 8)}.mp4`)}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 text-white text-xs font-bold hover:from-stone-800 hover:to-stone-700 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line" />
                      Video
                    </button>
                  </div>
                  {activeAudioUrl && (
                    <button
                      onClick={() => downloadFile(activeAudioUrl, `voiceover-${item.id.slice(0, 8)}.mp3`)}
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-orange-200 bg-white text-orange-700 text-xs font-bold hover:bg-orange-50 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line" />
                      Download Voiceover
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
